/**
 * Intelligence.jsx — Global Intelligence Search (Plan B)
 *
 * Natural language search across all extracted_line_items.
 * Returns a Claude insight paragraph + sortable data table.
 * Global scope — not locked to any project.
 *
 * v1.3 (May 2026) — UX fixes:
 *   - Search state (query, filters) synced to URL search params. Going
 *     back from a supplier/project page restores and re-runs the last
 *     search automatically.
 *   - Each result row shows the source document filename as a small
 *     clickable link that opens the PDF preview in a new tab.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || '';

// ─── DESIGN TOKENS (matches Projects.jsx exactly) ────────────────────────────
const V = {
  ink:    '#0c1830', ink2:'#2a3956', ink3:'#5a6680', ink4:'#8a93a6',
  paper:  '#fbfaf6', paper2:'#f3f1ea', paper3:'#e6e2d5', line:'#d8d3c2',
  gold:   '#c8a64b',
  good:   '#2a6f4f', goodSoft:'#e8f0eb',
  bad:    '#9c2b1a', badSoft:'#f4e8e5',
  warn:   '#a8731a', warnSoft:'#f5ecd9',
  serif:  "'Source Serif 4','Times New Roman',Georgia,serif",
  sans:   "'Inter',-apple-system,system-ui,sans-serif",
  mono:   "'JetBrains Mono',ui-monospace,monospace",
};

// ─── FONT LOADER ──────────────────────────────────────────────────────────────
function FontLoader() {
  useEffect(() => {
    if (document.getElementById('verdict-fonts')) return;
    const link = document.createElement('link');
    link.id   = 'verdict-fonts';
    link.href = 'https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,300;8..60,400;8..60,500;8..60,600;8..60,700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap';
    link.rel  = 'stylesheet';
    document.head.appendChild(link);
  }, []);
  return null;
}

// ─── EXAMPLE QUERIES ─────────────────────────────────────────────────────────
const EXAMPLES = [
  'Annual inspection',
  'CO2 cylinders',
  'Fire extinguishers',
  'Cylinder weight checking',
  'Detection systems',
  'Inspection services',
];

// ─── CATEGORIES (dropdown filter — full known set) ───────────────────────────
const CATEGORIES = [
  'anchor', 'steel', 'fuel', 'paint', 'electrical',
  'mechanical', 'civil', 'labour', 'equipment', 'safety', 'consumables', 'services',
];

// ─── YEARS ────────────────────────────────────────────────────────────────────
const YEARS = Array.from({ length: 5 }, (_, i) => String(new Date().getFullYear() - i));

// ─── TABLE COLUMNS ────────────────────────────────────────────────────────────
const COLUMNS = [
  { key: 'description',   label: 'Description' },
  { key: 'supplier_name', label: 'Supplier'     },
  { key: 'project_name',  label: 'Project'      },
  { key: 'doc_date',      label: 'Date'         },
  { key: 'unit_price',    label: 'Unit Price'   },
  { key: 'total_price',   label: 'Total'        },
  { key: 'quantity',      label: 'Qty'          },
  { key: 'category_tag',  label: 'Category'     },
];

// ─── NAV ITEMS ────────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { label: 'Negotiations', path: '/'             },
  { label: 'The Room',     path: null            },
  { label: 'Suppliers',    path: '/suppliers'    },
  { label: 'Documents',    path: null            },
  { label: 'Reports',      path: null            },
  { label: 'Intelligence', path: '/intelligence' },
];

// ─── SESSION CACHE ────────────────────────────────────────────────────────────
// Persists the last search result for 5 minutes so navigating away and pressing
// Back restores results instantly — no round-trip to the API.

const CACHE_KEY = 'verdict_intelligence_v1';
const CACHE_TTL = 5 * 60 * 1000;

function saveCache(q, pid, cat, yr, data) {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ q, pid, cat, yr, data, ts: Date.now() })); } catch(e) {}
}

function loadCache(q, pid, cat, yr) {
  try {
    const c = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    if (!c) return null;
    if (c.q !== q || c.pid !== pid || c.cat !== cat || c.yr !== yr) return null;
    if (Date.now() - c.ts > CACHE_TTL) return null;
    return c.data;
  } catch(e) { return null; }
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function Intelligence() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [query,         setQuery]         = useState('');
  const [category,      setCategory]      = useState('');
  const [year,          setYear]          = useState('');
  const [projectId,     setProjectId]     = useState('');
  const [loading,       setLoading]       = useState(false);
  const [result,        setResult]        = useState(null);
  const [error,         setError]         = useState(null);
  const [projects,      setProjects]      = useState([]);
  const [topCategories, setTopCategories] = useState([]);
  const [sortKey,       setSortKey]       = useState('doc_date');
  const [sortDir,       setSortDir]       = useState('desc');
  const [previewDoc,    setPreviewDoc]    = useState(null); // { id, name } | null

  // Fetch projects list for the filter dropdown
  useEffect(() => {
    fetch(`${API}/api/projects`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setProjects(d.projects || []))
      .catch(() => {});
  }, [token]);

  // Fetch category-tag distribution for the "what's in your data" hint
  useEffect(() => {
    fetch(`${API}/api/intelligence/categories`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setTopCategories(d.categories || []))
      .catch(() => {});
  }, [token]);

  // ── RESTORE FROM URL on mount ──────────────────────────────────────────────
  // If the URL has ?q=..., check the session cache first — if the params match
  // a recent result, restore immediately. Otherwise run the search normally.
  useEffect(() => {
    const urlQ    = searchParams.get('q') || '';
    const urlPid  = searchParams.get('project_id') || '';
    const urlCat  = searchParams.get('category') || '';
    const urlYear = searchParams.get('year') || '';

    if (urlQ) {
      setQuery(urlQ); setProjectId(urlPid); setCategory(urlCat); setYear(urlYear);
      const cached = loadCache(urlQ, urlPid, urlCat, urlYear);
      if (cached) {
        setResult(cached); // instant restore — no spinner, no API call
      } else {
        runSearch(urlQ, { projectId: urlPid, category: urlCat, year: urlYear });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── SEARCH ─────────────────────────────────────────────────────────────────
  // Accepts optional query override + filter overrides so chip clicks and
  // the URL-restore mount effect can pass values directly (React setState
  // is async, so reading component state immediately after setting it would
  // return stale values).

  async function runSearch(queryOverride, filterOverrides = {}) {
    const q = (queryOverride !== undefined ? queryOverride : query).trim();
    if (!q) return;

    // Merge: overrides → current component state
    const pid = filterOverrides.projectId !== undefined ? filterOverrides.projectId : projectId;
    const cat = filterOverrides.category  !== undefined ? filterOverrides.category  : category;
    const yr  = filterOverrides.year      !== undefined ? filterOverrides.year      : year;

    // Sync React state
    if (queryOverride !== undefined)               setQuery(queryOverride);
    if (filterOverrides.projectId !== undefined)    setProjectId(filterOverrides.projectId);
    if (filterOverrides.category  !== undefined)    setCategory(filterOverrides.category);
    if (filterOverrides.year      !== undefined)    setYear(filterOverrides.year);

    // Sync URL — replace so multiple searches don't pollute history
    const params = new URLSearchParams();
    params.set('q', q);
    if (pid) params.set('project_id', pid);
    if (cat) params.set('category', cat);
    if (yr)  params.set('year', yr);
    setSearchParams(params, { replace: true });

    // Run
    setLoading(true); setError(null); setResult(null);
    try {
      const res = await fetch(`${API}/api/intelligence`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({
          query: q,
          project_id: pid || undefined,
          category:   cat || undefined,
          year:       yr  || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Search failed');
      setResult(data);
      saveCache(q, pid, cat, yr, data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  function sortedRows() {
    if (!result?.rows?.length) return [];
    return [...result.rows].sort((a, b) => {
      let av = a[sortKey] ?? '', bv = b[sortKey] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  }

  function openDocPreview(documentId, documentName) {
    if (!documentId) return;
    setPreviewDoc({ id: documentId, name: documentName || 'Document' });
  }

  const fmtPrice = (v, cur) => v != null
    ? `${cur || ''} ${Number(v).toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 })}`
    : '—';
  const fmtDate = d => d ? d.substring(0, 10) : '—';
  const hasFilters = projectId || category || year;

  return (
    <div style={s.wrap}>
      <FontLoader />

      {/* ── DOCUMENT PREVIEW MODAL ───────────────────────────────────────── */}
      {previewDoc && (
        <div style={s.overlay} onClick={() => setPreviewDoc(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalBar}>
              <span style={{ fontSize:12, fontFamily:V.mono, color:V.ink3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1, marginRight:12 }}>
                {previewDoc.name}
              </span>
              <a
                href={`${API}/api/documents/${previewDoc.id}/file?token=${token}`}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize:11, color:V.ink4, fontFamily:V.sans, marginRight:16, textDecoration:'underline', textUnderlineOffset:2, whiteSpace:'nowrap' }}
              >
                Open in tab
              </a>
              <button onClick={() => setPreviewDoc(null)} style={s.modalClose}>✕</button>
            </div>
            <iframe
              src={`${API}/api/documents/${previewDoc.id}/file?token=${token}`}
              style={s.iframe}
              title={previewDoc.name}
            />
          </div>
        </div>
      )}

      {/* ── TOP NAV — pixel-matches Projects.jsx ─────────────────────────── */}
      <div style={s.nav}>
        <div style={s.wordmark}>
          <span style={s.wordmarkV}>V</span>
          <span style={s.wordmarkRest}>erdict</span>
        </div>
        <div style={s.wordmarkDivider} />
        <div style={s.wordmarkTag}>Negotiation Intelligence</div>
        <div style={s.navItems}>
          {NAV_ITEMS.map(item => {
            const active = window.location.pathname === item.path;
            return (
              <div key={item.label}
                style={{ ...s.navItem, color: active ? V.ink : V.ink3, borderBottom: active ? `2px solid ${V.gold}` : '2px solid transparent' }}
                onClick={() => item.path && navigate(item.path)}
              >
                {item.label}
              </div>
            );
          })}
        </div>
        <div style={s.navRight}>
          <span style={s.navUser}>{user?.email}</span>
          <button style={s.navSignOut} onClick={logout}>Sign out</button>
        </div>
      </div>

      {/* ── PAGE BODY ─────────────────────────────────────────────────────── */}
      <div style={s.body}>

        {/* Header */}
        <div style={s.pageHead}>
          <div style={s.pageEyebrow}>Global Search</div>
          <div style={s.pageTitle}>Intelligence</div>
          <div style={s.pageSub}>Search extracted pricing data across all projects, suppliers, and documents</div>
        </div>

        {/* Search bar */}
        <div style={{ display:'flex', gap:12 }}>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runSearch()}
            placeholder="e.g. Annual inspection, CO2 cylinders, τσιμέντο, fire extinguishers…"
            style={s.searchInput}
            onFocus={e => (e.target.style.borderColor = V.gold)}
            onBlur={e  => (e.target.style.borderColor = V.line)}
          />
          <button
            onClick={() => runSearch()}
            disabled={loading || !query.trim()}
            style={{ ...s.searchBtn, background: loading || !query.trim() ? V.ink4 : V.ink, cursor: loading || !query.trim() ? 'not-allowed' : 'pointer' }}
          >
            {loading ? 'Searching…' : 'Search'}
          </button>
        </div>

        {/* Filters */}
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
          <select value={projectId} onChange={e => { const v = e.target.value; setProjectId(v); if (query.trim()) runSearch(undefined, { projectId: v }); }} style={s.select}>
            <option value="">All Projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={category} onChange={e => { const v = e.target.value; setCategory(v); if (query.trim()) runSearch(undefined, { category: v }); }} style={s.select}>
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
          </select>
          <select value={year} onChange={e => { const v = e.target.value; setYear(v); if (query.trim()) runSearch(undefined, { year: v }); }} style={s.select}>
            <option value="">All Years</option>
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {hasFilters && (
            <button onClick={() => { setProjectId(''); setCategory(''); setYear(''); if (query.trim()) runSearch(undefined, { projectId: '', category: '', year: '' }); }} style={s.clearBtn}>
              Clear filters
            </button>
          )}
        </div>

        {/* Empty state */}
        {!result && !loading && !error && (
          <div style={{ textAlign:'center', padding:'48px 0 60px' }}>
            <div style={{ fontSize:10, fontWeight:600, letterSpacing:2, color:V.ink4, textTransform:'uppercase', fontFamily:V.sans, marginBottom:20 }}>
              Try a query to get started
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:10, justifyContent:'center', maxWidth:680, margin:'0 auto' }}>
              {EXAMPLES.map(ex => (
                <button key={ex} onClick={() => runSearch(ex)} style={s.chip}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = V.ink2; e.currentTarget.style.color = V.ink; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = V.line; e.currentTarget.style.color = V.ink3; }}
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ textAlign:'center', padding:'72px 0', color:V.ink4, fontFamily:V.sans, fontSize:13 }}>
            Querying database · Interpreting your query · Analysing with AI…
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div style={{ padding:'14px 18px', background:'#f4e8e5', border:`1px solid #e0b0a8`, borderRadius:4, color:V.bad, fontSize:13, fontFamily:V.sans }}>
            {error}
          </div>
        )}

        {/* Results */}
        {result && !loading && (
          <>
            {/* Provenance banner — only when Claude expansion ran */}
            {result.interpretation && (
              <div style={s.provenanceBanner}>
                <div style={s.provenanceLabel}>AI Interpretation</div>
                <div style={s.provenanceText}>
                  Interpreted as: <span style={{ color:V.ink, fontWeight:500 }}>{result.interpretation.interpreted_as}</span>
                  {result.interpretation.search_keywords?.length > 0 && (
                    <>
                      <span style={{ color:V.ink4, margin:'0 8px' }}>·</span>
                      Searched for:{' '}
                      <span style={{ fontFamily:V.mono, fontSize:12, color:V.ink2 }}>
                        {result.interpretation.search_keywords.join(', ')}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Insight card */}
            {result.insight && (
              <div style={s.insightCard}>
                <div style={s.insightLabel}>Intelligence Insight</div>
                <p style={s.insightText}>{result.insight}</p>
              </div>
            )}

            {/* Row count */}
            <div style={{ fontSize:10, fontWeight:600, letterSpacing:2, color:V.ink4, textTransform:'uppercase', fontFamily:V.sans }}>
              <span style={{ fontFamily:V.mono, color:V.ink, fontSize:14, fontWeight:500, letterSpacing:-0.5, textTransform:'none' }}>{result.row_count}</span>
              {' '}record{result.row_count !== 1 ? 's' : ''} found
              {result.keyword && <> for <span style={{ fontFamily:V.mono, color:V.ink2, textTransform:'none', fontWeight:400, letterSpacing:0 }}>"{result.keyword}"</span></>}
              {result.detected_category && <> · <span style={{ color:V.gold, textTransform:'none', fontWeight:400, letterSpacing:0 }}>{result.detected_category}</span></>}
            </div>

            {/* No rows — Claude's tailored note first, then category discovery */}
            {result.rows.length === 0 && (
              <div style={{ textAlign:'center', padding:'48px 24px' }}>
                {result.interpretation?.note_for_user ? (
                  <>
                    <div style={{ fontSize:15, color:V.ink2, fontFamily:V.sans, marginBottom:8, lineHeight:1.55, maxWidth:560, margin:'0 auto 8px' }}>
                      {result.interpretation.note_for_user}
                    </div>
                    <div style={{ fontSize:13, color:V.ink4, fontFamily:V.sans, marginBottom:28 }}>
                      Or browse the categories below.
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize:15, color:V.ink2, fontFamily:V.sans, marginBottom:8 }}>
                      No matching line items found.
                    </div>
                    <div style={{ fontSize:13, color:V.ink4, fontFamily:V.sans, marginBottom:28, lineHeight:1.6 }}>
                      Try a broader search term or clear your filters.
                    </div>
                  </>
                )}
                {topCategories.length > 0 && (
                  <>
                    <div style={{ fontSize:10, fontWeight:600, letterSpacing:2, color:V.ink4, textTransform:'uppercase', fontFamily:V.sans, marginBottom:14 }}>
                      What's in your data
                    </div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:10, justifyContent:'center', maxWidth:680, margin:'0 auto' }}>
                      {topCategories.slice(0, 6).map(cat => (
                        <button
                          key={cat.tag}
                          onClick={() => runSearch(cat.tag, { category: '' })}
                          style={s.chip}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = V.ink2; e.currentTarget.style.color = V.ink; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = V.line; e.currentTarget.style.color = V.ink3; }}
                        >
                          {cat.tag}
                          <span style={{ fontFamily:V.mono, color:V.ink4, fontSize:11, marginLeft:8 }}>{cat.count}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Table */}
            {result.rows.length > 0 && (
              <div style={{ overflowX:'auto', border:`0.5px solid ${V.line}`, borderRadius:6, background:V.paper }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13, fontFamily:V.sans }}>
                  <thead>
                    <tr style={{ background:V.paper2, borderBottom:`1px solid ${V.line}` }}>
                      {COLUMNS.map(col => (
                        <th key={col.key} onClick={() => toggleSort(col.key)} style={s.th}>
                          {col.label}
                          <span style={{ color: sortKey === col.key ? V.gold : V.ink4, marginLeft:4 }}>
                            {sortKey === col.key ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows().map((row, i) => (
                      <tr key={row.id || i} style={{ borderBottom:`0.5px solid ${V.line}`, background: i % 2 === 0 ? V.paper : V.paper2 }}>
                        <td style={{ ...s.td, maxWidth:280 }}>
                          <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:V.ink }}>{row.description || '—'}</div>
                          {row.document_id && row.original_name && (
                            <div
                              onClick={() => openDocPreview(row.document_id, row.original_name)}
                              style={s.docLink}
                              title={`Preview: ${row.original_name}`}
                            >
                              {row.original_name}
                            </div>
                          )}
                        </td>
                        <td style={s.td}>
                          {row.supplier_name
                            ? <span onClick={() => row.supplier_id && navigate(`/suppliers/${row.supplier_id}`)} style={{ color: row.supplier_id ? V.gold : V.ink2, cursor: row.supplier_id ? 'pointer' : 'default', textDecoration: row.supplier_id ? 'underline' : 'none', textUnderlineOffset:2 }}>{row.supplier_name}</span>
                            : <span style={{ color:V.ink4 }}>—</span>}
                        </td>
                        <td style={{ ...s.td, maxWidth:200 }}>
                          <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {row.project_name
                              ? <span onClick={() => row.project_id && navigate(`/projects/${row.project_id}`)} style={{ color: row.project_id ? V.ink2 : V.ink4, cursor: row.project_id ? 'pointer' : 'default' }}>{row.project_name}</span>
                              : <span style={{ color:V.ink4 }}>Standalone</span>}
                          </div>
                        </td>
                        <td style={{ ...s.td, fontFamily:V.mono, color:V.ink3, fontSize:12, whiteSpace:'nowrap' }}>{fmtDate(row.doc_date)}</td>
                        <td style={{ ...s.td, fontFamily:V.mono, color:V.ink, fontSize:12, whiteSpace:'nowrap' }}>{fmtPrice(row.unit_price, row.currency)}</td>
                        <td style={{ ...s.td, fontFamily:V.mono, color:V.ink, fontSize:12, whiteSpace:'nowrap', fontWeight: row.total_price ? 500 : 400 }}>{fmtPrice(row.total_price, row.currency)}</td>
                        <td style={{ ...s.td, fontFamily:V.mono, color:V.ink3, fontSize:12, whiteSpace:'nowrap' }}>{row.quantity != null ? `${row.quantity} ${row.unit || ''}`.trim() : '—'}</td>
                        <td style={s.td}>
                          <span style={{ fontSize:10, padding:'2px 8px', borderRadius:3, fontFamily:V.mono, letterSpacing:'0.05em', background:V.paper3, color:V.ink3 }}>
                            {row.category_tag || 'other'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────────────
const s = {
  wrap:             { minHeight:'100vh', background:V.paper2, fontFamily:V.sans, color:V.ink },
  nav:              { background:V.paper, borderBottom:`1px solid ${V.line}`, height:60, display:'flex', alignItems:'center', padding:'0 24px', gap:0 },
  wordmark:         { display:'flex', alignItems:'baseline', fontFamily:V.serif, fontSize:26, fontWeight:600, letterSpacing:-0.5, lineHeight:1 },
  wordmarkV:        { color:V.gold },
  wordmarkRest:     { color:V.ink },
  wordmarkDivider:  { width:1, height:18, background:V.line, margin:'0 12px' },
  wordmarkTag:      { fontSize:10, fontWeight:500, letterSpacing:2.5, textTransform:'uppercase', color:V.ink3, marginRight:32, whiteSpace:'nowrap' },
  navItems:         { display:'flex', gap:0, height:60, alignItems:'center' },
  navItem:          { padding:'0 16px', fontSize:13, fontWeight:500, cursor:'pointer', height:60, display:'flex', alignItems:'center', transition:'color 0.15s', letterSpacing:0.1 },
  navRight:         { marginLeft:'auto', display:'flex', alignItems:'center', gap:14 },
  navUser:          { fontSize:12, color:V.ink4 },
  navSignOut:       { background:'none', border:'none', fontSize:12, color:V.ink3, cursor:'pointer', padding:0, fontFamily:V.sans },
  body:             { padding:'36px 48px 100px', maxWidth:1440, margin:'0 auto', width:'100%', display:'flex', flexDirection:'column', gap:24, boxSizing:'border-box' },
  pageHead:         { paddingBottom:24, borderBottom:`1px solid ${V.line}` },
  pageEyebrow:      { fontSize:10, fontWeight:500, letterSpacing:2.5, textTransform:'uppercase', color:V.ink3, marginBottom:8, fontFamily:V.sans },
  pageTitle:        { fontFamily:V.serif, fontSize:42, fontWeight:400, color:V.ink, lineHeight:1.12, letterSpacing:-0.5, marginBottom:10 },
  pageSub:          { fontSize:13, color:V.ink3, lineHeight:1.5, maxWidth:480 },
  searchInput:      { flex:1, padding:'11px 16px', fontSize:14, border:`1px solid ${V.line}`, borderRadius:4, background:V.paper, fontFamily:V.sans, color:V.ink, outline:'none', transition:'border-color 0.15s' },
  searchBtn:        { padding:'11px 28px', color:V.paper, border:'none', borderRadius:4, fontFamily:V.sans, fontSize:13, fontWeight:500, flexShrink:0, transition:'background 0.15s', letterSpacing:0.1 },
  select:           { padding:'7px 12px', border:`1px solid ${V.line}`, borderRadius:4, background:V.paper, fontFamily:V.sans, fontSize:13, color:V.ink2, cursor:'pointer', outline:'none' },
  clearBtn:         { padding:'7px 12px', border:`1px solid ${V.line}`, borderRadius:4, background:'none', fontFamily:V.sans, fontSize:13, color:V.ink3, cursor:'pointer' },
  chip:             { padding:'8px 16px', border:`1px solid ${V.line}`, borderRadius:20, background:V.paper, fontFamily:V.sans, fontSize:13, color:V.ink3, cursor:'pointer', transition:'all 0.15s' },
  provenanceBanner: { padding:'12px 18px', background:V.paper, border:`1px solid ${V.line}`, borderLeft:`3px solid ${V.ink2}`, borderRadius:'0 4px 4px 0' },
  provenanceLabel:  { fontSize:9, fontWeight:700, letterSpacing:2, color:V.ink3, textTransform:'uppercase', fontFamily:V.sans, marginBottom:6 },
  provenanceText:   { fontSize:13, color:V.ink3, fontFamily:V.sans, lineHeight:1.5 },
  insightCard:      { padding:'18px 22px', background:V.paper, borderLeft:`3px solid ${V.gold}`, borderRadius:'0 4px 4px 0', boxShadow:'0 1px 4px rgba(12,24,48,0.05)' },
  insightLabel:     { fontSize:9, fontWeight:700, letterSpacing:2, color:V.gold, textTransform:'uppercase', fontFamily:V.sans, marginBottom:8 },
  insightText:      { margin:0, fontSize:14, lineHeight:1.75, color:V.ink, fontFamily:V.sans },
  th:               { padding:'10px 14px', textAlign:'left', fontWeight:600, color:V.ink3, cursor:'pointer', whiteSpace:'nowrap', userSelect:'none', fontSize:10, letterSpacing:'0.08em', textTransform:'uppercase' },
  td:               { padding:'10px 14px', color:V.ink2 },
  docLink:          { fontSize:11, color:V.ink4, cursor:'pointer', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginTop:2, textDecoration:'underline', textDecorationColor:V.line, textUnderlineOffset:2, maxWidth:260 },
  overlay:          { position:'fixed', inset:0, background:'rgba(12,24,48,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 },
  modal:            { background:V.paper, borderRadius:6, width:'82vw', height:'88vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 24px 80px rgba(12,24,48,0.3)' },
  modalBar:         { display:'flex', alignItems:'center', padding:'10px 14px', borderBottom:`1px solid ${V.line}`, flexShrink:0, background:V.paper2 },
  modalClose:       { background:'none', border:'none', fontSize:18, cursor:'pointer', color:V.ink3, padding:'0 2px', fontFamily:V.sans, lineHeight:1, flexShrink:0 },
  iframe:           { flex:1, border:'none', width:'100%' },
};
